import * as React from "react"
import { type ButtonVariant } from "@digits-shared/DesignSystem/Button"
import errorHelper from "@digits-shared/helpers/errorHelper"
import styled from "styled-components"
import { ContinueWithGoogle } from "src/shared/components/Session/ContinueWithGoogle"
import { GISLogin } from "src/shared/components/Session/GISLogin"

/*
  STYLES
*/

const GoogleLoginContainer = styled.div`
  width: 100%;
`

/*
  INTERFACE
*/

interface GoogleLoginProps {
  onStart?: () => void
  onCancel?: () => void
  onSuccess?: (code: string) => Promise<unknown>
  onFailure?: (error: string, code: string) => void
  onAutoLoadFinished?: () => void
  render?: (props: { onClick: () => void; disabled?: boolean; text?: string }) => React.JSX.Element
  disabled?: boolean
  variant?: ButtonVariant
  loading?: boolean
}

/*
  COMPONENTS
*/

export const GoogleLogin = React.forwardRef<HTMLButtonElement, GoogleLoginProps>(
  ({ onStart, onCancel, onSuccess, onFailure, render, disabled, variant, loading }, ref) => {
    const [signingIn, setSigningIn] = React.useState(false)

    React.useEffect(() => {
      if (signingIn) {
        onStart?.()
      }
    }, [onStart, signingIn])

    const onGoogleSignInSuccess = React.useCallback(
      (response: google.accounts.oauth2.CodeResponse) => {
        onSuccess?.(response.code).finally(() => setSigningIn(false))
      },
      [onSuccess]
    )

    const onGoogleSignInFailure = React.useCallback(
      (error: { error: string; type?: string }) => {
        setSigningIn(false)
        const code = error?.type || error?.error
        switch (code) {
          case "popup_blocked_by_browser":
          case "popup_failed_to_open":
            TrackJS?.console.info("Popup blocker signing into Google", error)
            onFailure?.("It looks like your browser blocked a pop-up.", code)
            return

          case "popup_closed_by_user":
          case "popup_closed":
            TrackJS?.console.info("Popup closed by user", error)
            onCancel?.()
            return

          case "idpiframe_initialization_failed":
            TrackJS?.console.info("User has third-party cookies disabled", error)
            onFailure?.("It looks like your browser has third-party cookies disabled.", code)
            return

          // ignore 'script errors' that occur inside google's iframe. There is nothing we can do about it.
          // Likely triggered by "Googlebot"
          case "error":
          case "unknown":
            onFailure?.("Error 350.", code)
            return

          default:
            TrackJS?.console.info("Error signing into Google", errorHelper.stringifyError(error))
            onFailure?.("Error 350.", code || "unknown")
        }
      },
      [onCancel, onFailure]
    )

    return (
      <GoogleLoginContainer>
        <GISLogin
          signingIn={signingIn}
          setSigningIn={setSigningIn}
          onSuccess={onGoogleSignInSuccess}
          onFailure={onGoogleSignInFailure}
          render={render || ContinueWithGoogle}
          variant={variant}
          loading={loading}
          disabled={disabled}
        />
      </GoogleLoginContainer>
    )
  }
)
